import PropTypes from "prop-types";
import { m } from "framer-motion";
import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
// @mui
import { Container, Typography } from "@mui/material";
// components
import { MotionContainer, varBounce } from "../../components/animate";
// assets
import { ForbiddenIllustration } from "../../assets/illustrations";
//
import { useAuthContext } from "./useAuthContext";
import LoadingScreen from "../../components/loading-screen/LoadingScreen";

// ----------------------------------------------------------------------

export const RoleBasedGuard = ({ hasContent, roles, children }) => {
  // Logic here to get current user role
  const { user } = useAuthContext();
  const location = useLocation();
  const [loadingTimedOut, setLoadingTimedOut] = useState(false);
  const AUTH_RETURN_PATH_KEY = "auth0_return_path";

  // Don't block UI forever if auth/user hydration is stuck (e.g. expired session)
  useEffect(() => {
    if (user) {
      setLoadingTimedOut(false);
      return;
    }
    const t = setTimeout(() => setLoadingTimedOut(true), 8000);
    return () => clearTimeout(t);
  }, [user]);

  if (!user && loadingTimedOut) {
    const returnTo = location.pathname || "/logout";
    // Preserve the desired post-login path via sessionStorage (used by AuthCallbackPage).
    try {
      if (returnTo && !returnTo.startsWith("/auth")) {
        sessionStorage.setItem(AUTH_RETURN_PATH_KEY, returnTo);
      }
    } catch (e) {
      // ignore
    }

    // Important: keep returnTo free of query params so it matches Auth0 Allowed Logout URLs.
    const logoutReturnTo = `${window.location.origin}/logout`;
    return (
      <Navigate
        to={`/auth/logout-init?returnTo=${encodeURIComponent(logoutReturnTo)}`}
        replace
      />
    );
  }

  const currentRoles = user?.roles.map(({ name }) => name);
  const checkPrivilege = () => {
    let numMatch = 0;

    if (typeof roles !== "undefined" && Array.isArray(currentRoles)) {
      currentRoles.map((x) => {
        if (roles.includes(x)) numMatch += 1;
      });
    }
    return numMatch > 0;
  };

  if (!user) {
    return hasContent ? <LoadingScreen /> : null;
  }

  if (typeof roles !== "undefined" && !checkPrivilege()) {
    return hasContent ? (
      <Container component={MotionContainer} sx={{ textAlign: "center" }}>
        <m.div variants={varBounce().in}>
          <Typography variant="h3" paragraph>
            Permission Denied
          </Typography>
        </m.div>

        <m.div variants={varBounce().in}>
          <Typography sx={{ color: "text.secondary" }}>
            You do not have permission to access this page
          </Typography>
        </m.div>

        <m.div variants={varBounce().in}>
          <ForbiddenIllustration sx={{ height: 260, my: { xs: 5, sm: 10 } }} />
        </m.div>
      </Container>
    ) : null;
  }

  return <> {children} </>;
};

RoleBasedGuard.propTypes = {
  children: PropTypes.node,
  hasContent: PropTypes.bool,
  roles: PropTypes.arrayOf(PropTypes.string),
};
